// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Tile } from '@carbon/react';
import type { HitlApproval } from '../../../types';

export interface LiveUnplannedSummaryProps {
  /** Today's approval, when there is one to open. */
  approval?: HitlApproval;
  hasBaseline: boolean;
  /** Whether a plan is waiting on the Tomorrow tab. */
  hasPendingReview: boolean;
}

/**
 * The side panel beside {@link LiveUnplannedHero}: the chart legend in words,
 * because with no plan running the absence of a blue curve is the point, plus
 * a route to whatever decision can still be made.
 */
export default function LiveUnplannedSummary({
  approval,
  hasBaseline,
  hasPendingReview,
}: LiveUnplannedSummaryProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = `${location.pathname}${location.search}`;

  return (
    <Tile className="live-summary">
      <h3 className="live-summary-title">What you&apos;re seeing</h3>
      <p className="live-summary-body">
        {hasBaseline
          ? 'Grey dashed: the load expected with no optimization. Green: measured, updated every minute.'
          : 'Green: measured, updated every minute. Nothing to compare it against today.'}
      </p>

      <div className="live-summary-actions">
        <Button
          kind="tertiary"
          size="md"
          disabled={!approval}
          aria-describedby={approval ? undefined : 'live-summary-no-rec'}
          onClick={() =>
            approval && navigate(`/optimizations/${approval.approvalId}`, { state: { from } })
          }
        >
          View today&apos;s recommendation
        </Button>
        {hasPendingReview && (
          <Button kind="tertiary" size="md" onClick={() => navigate('/recommendations/tomorrow')}>
            Review pending plans
          </Button>
        )}
      </div>
      {!approval && (
        <p id="live-summary-no-rec" className="live-summary-note">
          Helios didn&apos;t raise a recommendation for today, so there is nothing to open.
        </p>
      )}
    </Tile>
  );
}
